import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import { readState, subscribe, unsubscribe, type PushState } from '../../lib/push'

/**
 * Benachrichtigungen ein- und ausschalten.
 *
 * Gefragt wird erst auf Knopfdruck. Ungefragt darf keine Seite nach der
 * Erlaubnis fragen — und wer einmal abgelehnt hat, kann es im Browser nur
 * mühsam zurücknehmen, deshalb soll die Frage bewusst kommen.
 *
 * Die Karte erscheint nur, wenn der Server Benachrichtigungen überhaupt
 * eingerichtet hat. Ein Schalter, der nichts bewirkt, ist schlimmer als
 * keiner.
 */
export function PushCard() {
  const [server, setServer] = useState<{ enabled: boolean } | null>(null)
  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        setServer(await api.get<{ enabled: boolean }>('/api/push/key'))
        setState(await readState())
      } catch {
        setServer({ enabled: false })
      }
    })()
  }, [])

  if (!server?.enabled || !state) return null

  async function umschalten() {
    setBusy(true)
    setProblem(null)
    setMeldung(null)
    try {
      if (state?.subscribed) {
        await unsubscribe()
      } else {
        await subscribe()
      }
      setState(await readState())
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  async function probe() {
    setBusy(true)
    setProblem(null)
    setMeldung(null)
    try {
      const antwort = await api.post<{ devices: number }>('/api/push/test')
      setMeldung(
        antwort.devices === 1
          ? 'Verschickt — sie sollte gleich ankommen.'
          : `An ${antwort.devices} Geräte verschickt.`,
      )
    } catch (fehler) {
      setProblem(fehler instanceof Error ? fehler.message : 'Das hat nicht geklappt.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Benachrichtigungen</h2>

      {!state.supported ? (
        <p className="card__lead" style={{ marginBottom: 0 }}>
          {state.needsInstall
            ? 'Auf dem iPhone erst, wenn die App über Teilen → Zum Home-Bildschirm installiert ist. Öffne sie danach von dort.'
            : 'Dieser Browser kann keine Benachrichtigungen empfangen.'}
        </p>
      ) : (
        <>
          <p className="card__lead">
            {state.subscribed
              ? 'Dieses Gerät bekommt eine Nachricht, wenn es etwas Neues gibt. Für einzelne Reisen lässt sich das dort abschalten.'
              : 'Bekomme eine Nachricht, wenn jemand etwas Neues schreibt — auch wenn die App zu ist.'}
          </p>

          {state.stale && (
            <p className="notice" role="alert">
              Die Anmeldung dieses Geräts gehört zu einem älteren Schlüssel des
              Servers — der Push-Dienst weist sie ab. Einmal einschalten
              erneuert sie.
            </p>
          )}

          {/* War schon einmal an und ist es nicht mehr. Das passiert ohne
              Zutun -- iOS räumt bei einer selten benutzten Web-App auf --, und
              man merkt es sonst erst daran, dass nichts mehr ankommt. Die
              Anwendung versucht es beim Start von selbst; hier steht, was
              übrig bleibt, wenn auch die Erlaubnis weg ist. */}
          {state.wanted && !state.subscribed && !state.stale && (
            <p className="notice" role="alert">
              Dieses Gerät war schon einmal angemeldet, ist es aber nicht mehr.
              Einmal einschalten stellt es wieder her.
            </p>
          )}

          {state.permission === 'denied' && !state.subscribed && (
            <p className="notice" role="alert">
              Der Browser hat die Erlaubnis gesperrt. Sie lässt sich in seinen
              Einstellungen für diese Seite wieder erteilen.
            </p>
          )}

          {problem && <p className="notice" role="alert">{problem}</p>}
          {meldung && (
            <p className="card__lead" style={{ color: 'var(--accent)' }}>{meldung}</p>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={busy || (state.permission === 'denied' && !state.subscribed)}
            onClick={() => void umschalten()}
          >
            {busy
              ? 'Einen Moment …'
              : state.subscribed
                ? 'Auf diesem Gerät abschalten'
                : 'Auf diesem Gerät einschalten'}
          </button>

          {state.subscribed && (
            <button
              className="button button--outline"
              type="button"
              style={{ marginTop: 10 }}
              disabled={busy}
              onClick={() => void probe()}
            >
              Probe senden
            </button>
          )}
        </>
      )}
    </div>
  )
}
